import { Check, Sparkles, Star, Crown, ArrowUpRight } from "lucide-react";
import { waLink } from "../../lib/wa";

const tiers = [
    {
        key: "starter",
        name: "Starter",
        icon: Sparkles,
        price: "Rp500.000",
        priceNote: "/ proyek",
        tagline:
            "Landing page profesional untuk UMKM, personal brand, atau bisnis kecil.",
        features: [
            "1 halaman",
            "Desain modern & mobile friendly",
            "Tombol WhatsApp",
            "Form kontak",
            "Google Maps",
            "SEO dasar",
            "SSL HTTPS",
            "Gratis revisi 2x",
        ],
        cta: "Pilih paket Starter",
        tone: "cream",
        popular: false,
    },
    {
        key: "business",
        name: "Business",
        icon: Star,
        price: "Rp1.500.000",
        priceNote: "/ proyek",
        tagline:
            "Website bisnis profesional untuk meningkatkan kredibilitas dan penjualan.",
        features: [
            "Hingga 5 halaman",
            "Desain custom premium",
            "Blog atau artikel",
            "Integrasi WhatsApp",
            "SEO on-page dasar",
            "Optimasi kecepatan",
            "Gratis revisi 3x",
        ],
        cta: "Pilih paket Business",
        tone: "primary",
        popular: true,
    },
    {
        key: "premium",
        name: "Premium",
        icon: Crown,
        price: "Rp3.000.000+",
        priceNote: "mulai dari",
        tagline:
            "Website custom dengan fitur lanjutan dan admin panel sesuai kebutuhan.",
        features: [
            "Halaman tak terbatas",
            "Dashboard admin",
            "Database (Supabase)",
            "Login admin",
            "CRUD data",
            "Upload gambar / file",
            "Advanced SEO",
            "Maintenance 30 hari",
        ],
        cta: "Diskusi paket Premium",
        tone: "dark",
        popular: false,
    },
];

const bonusList = [
    "Gratis konsultasi awal",
    "Setup domain & hosting",
    "Panduan penggunaan website",
    "Support setelah website selesai",
];

const toneStyles = {
    cream: {
        wrapper:
            "bg-jws-surface border border-jws-border text-jws-text hover:border-jws-text",
        accent: "text-jws-primary",
        check: "text-jws-primary",
        btn: "bg-jws-text text-jws-bg hover:bg-jws-primary",
    },
    primary: {
        wrapper:
            "bg-jws-primary text-jws-bg border border-jws-primary shadow-[0_24px_60px_-30px_rgba(168,60,34,0.7)]",
        accent: "text-jws-ochre",
        check: "text-jws-ochre",
        btn: "bg-jws-bg text-jws-text hover:bg-jws-ochre",
    },
    dark: {
        wrapper:
            "bg-jws-secondary text-jws-bg border border-jws-secondary hover:bg-[#234835]",
        accent: "text-jws-ochre",
        check: "text-jws-ochre",
        btn: "bg-jws-ochre text-jws-text hover:bg-jws-bg",
    },
};

export const Pricing = () => {
    return (
        <section
            id="harga"
            data-testid="pricing-section"
            className="py-20 md:py-32 relative bg-jws-bg"
        >
            <div className="max-w-7xl mx-auto px-5 md:px-10">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-10 mb-12 md:mb-16">
                    <div className="col-span-1 md:col-span-6">
                        <div className="text-xs uppercase tracking-[0.3em] text-jws-muted mb-4">
                            (03) Harga
                        </div>
                        <h2 className="font-display text-4xl md:text-6xl tracking-tight leading-[1]">
                            Harga transparan,
                            <br />
                            <span className="italic text-jws-primary">
                                tanpa biaya tersembunyi
                            </span>
                            .
                        </h2>
                    </div>
                    <div className="col-span-1 md:col-span-5 md:col-start-8 md:pt-4">
                        <p className="text-base md:text-lg text-jws-muted leading-relaxed">
                            Pilih paket yang paling sesuai dengan tahap bisnis
                            anda. Bingung mana yang cocok? Chat WhatsApp dulu,
                            saya bantu rekomendasi paket terbaik untuk
                            kebutuhan anda.
                        </p>
                    </div>
                </div>

                {/* Pricing cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6 items-stretch">
                    {tiers.map((t) => {
                        const s = toneStyles[t.tone];
                        const Icon = t.icon;
                        return (
                            <div
                                key={t.key}
                                data-testid={`pricing-${t.key}`}
                                className={`relative rounded-3xl p-7 md:p-8 flex flex-col transition-all duration-300 hover:-translate-y-1 ${s.wrapper} ${t.popular ? "md:scale-[1.03] md:-mt-4" : ""}`}
                            >
                                {t.popular && (
                                    <div
                                        data-testid="pricing-popular-badge"
                                        className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 bg-jws-ochre text-jws-text px-3.5 py-1 rounded-full text-xs font-semibold tracking-wide whitespace-nowrap"
                                    >
                                        <Star className="h-3.5 w-3.5 fill-current" />
                                        Paling Populer
                                    </div>
                                )}

                                <div className="flex items-center justify-between">
                                    <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] opacity-80">
                                        <Icon className="h-4 w-4" />
                                        {t.name}
                                    </div>
                                </div>

                                <div className="mt-6 md:mt-8">
                                    <div className="font-display text-4xl md:text-5xl tracking-tighter leading-none">
                                        {t.price}
                                    </div>
                                    <div className="mt-2 text-xs opacity-70">
                                        {t.priceNote}
                                    </div>
                                </div>

                                <p className="mt-5 md:mt-6 text-sm md:text-base opacity-90 leading-relaxed">
                                    {t.tagline}
                                </p>

                                <ul className="mt-6 md:mt-8 space-y-3 flex-1">
                                    {t.features.map((f) => (
                                        <li
                                            key={f}
                                            className="flex items-start gap-3 text-sm md:text-base"
                                        >
                                            <Check
                                                className={`h-5 w-5 shrink-0 mt-0.5 ${s.check}`}
                                                strokeWidth={2.5}
                                            />
                                            <span className="opacity-95">
                                                {f}
                                            </span>
                                        </li>
                                    ))}
                                </ul>

                                <a
                                    href={waLink(
                                        `Halo, saya tertarik dengan paket ${t.name} (${t.price}). Bisa diskusi lebih lanjut?`
                                    )}
                                    target="_blank"
                                    rel="noreferrer"
                                    data-testid={`pricing-cta-${t.key}`}
                                    className={`mt-7 md:mt-8 inline-flex items-center justify-between gap-3 rounded-full pl-5 pr-2 py-2.5 font-medium text-sm transition-colors ${s.btn}`}
                                >
                                    <span>{t.cta}</span>
                                    <span className="h-9 w-9 grid place-items-center rounded-full bg-jws-text/10 shrink-0">
                                        <ArrowUpRight className="h-4 w-4" />
                                    </span>
                                </a>
                            </div>
                        );
                    })}
                </div>

                {/* Bonus + Payment */}
                <div className="mt-14 md:mt-20 grid grid-cols-1 md:grid-cols-12 gap-5 md:gap-6">
                    <div
                        data-testid="pricing-bonus"
                        className="col-span-1 md:col-span-7 rounded-2xl bg-jws-surface2 border border-jws-border p-7 md:p-10"
                    >
                        <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-jws-primary">
                            <Sparkles className="h-4 w-4" />
                            Bonus untuk semua paket
                        </div>
                        <h3 className="mt-4 font-display text-2xl md:text-3xl tracking-tight">
                            Yang anda dapatkan tanpa biaya tambahan
                        </h3>
                        <ul className="mt-5 md:mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
                            {bonusList.map((b) => (
                                <li
                                    key={b}
                                    className="flex items-start gap-3 text-sm md:text-base text-jws-text"
                                >
                                    <Check
                                        className="h-5 w-5 shrink-0 mt-0.5 text-jws-primary"
                                        strokeWidth={2.5}
                                    />
                                    <span>{b}</span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div
                        data-testid="pricing-payment"
                        className="col-span-1 md:col-span-5 rounded-2xl bg-jws-text text-jws-bg p-7 md:p-10 flex flex-col"
                    >
                        <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-jws-ochre">
                            Sistem pembayaran
                        </div>
                        <h3 className="mt-4 font-display text-2xl md:text-3xl tracking-tight">
                            Aman &amp; sederhana
                        </h3>
                        <div className="mt-6 space-y-4 flex-1">
                            <div className="flex items-start gap-4">
                                <div className="h-9 w-9 grid place-items-center rounded-full bg-jws-ochre text-jws-text font-display text-sm font-bold shrink-0">
                                    50%
                                </div>
                                <div>
                                    <div className="font-medium">DP awal</div>
                                    <p className="text-sm text-jws-bg/70 mt-1">
                                        Untuk memulai proyek dan kunci slot pengerjaan.
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-start gap-4">
                                <div className="h-9 w-9 grid place-items-center rounded-full bg-jws-bg text-jws-text font-display text-sm font-bold shrink-0">
                                    50%
                                </div>
                                <div>
                                    <div className="font-medium">Pelunasan</div>
                                    <p className="text-sm text-jws-bg/70 mt-1">
                                        Dibayar setelah website selesai dan siap launching.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};
